import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Modal, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination, CircularProgress, Alert } from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Header from '../components/Header';
import { getInvestorLeads } from '../services/propertyService';
import { API_BASE_URL } from '../config/api';

const InvestPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    // State for API data and loading
    const [investorData, setInvestorData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    
    // Modal state
    const [selectedInvestor, setSelectedInvestor] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Fetch data from API
    useEffect(() => {
        const fetchInvestorData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const response = await getInvestorLeads(page, pageSize);
                
                if (response.success) {
                    setInvestorData(response.result.data || []);
                    setTotalPages(response.result.totalPages || 1);
                    setTotalRecords(response.result.totalRecords || 0);
                } else {
                    setError(response.message || 'Failed to fetch investor data');
                    setInvestorData([]);
                }
            } catch (err) {
                setError('An error occurred while fetching data');
                console.error('Error fetching investor data:', err);
            } finally {
                setLoading(false);
            }
        };
        
        fetchInvestorData();
    }, [page, pageSize]);

    // Handle page change
    const handlePageChange = (event, value) => {
        setPage(value);
    };

    // Open view modal
    const openViewModal = (investor) => {
        setSelectedInvestor(investor);
        setCurrentImageIndex(0);
        setIsViewModalOpen(true);
    };

    // Close view modal
    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setSelectedInvestor(null);
    };
    
    // Process image URL to ensure it's complete
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        return `${API_BASE_URL}/uploads/${imagePath}`;
    };
    
    // Check if file is an image
    const isImage = (path) => {
        if (!path) return false;
        const ext = path.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
    };
    
    // Check if file is a video
    const isVideo = (path) => {
        if (!path) return false;
        const ext = path.split('.').pop().toLowerCase();
        return ['mp4', 'webm', 'ogg', 'mov'].includes(ext);
    };
    
    // Get all media (images and videos) from investor data
    const getInvestorMedia = (investor) => {
        if (!investor) return { images: [], videos: [] };
        
        let mediaArray = [];
        
        if (investor.images && Array.isArray(investor.images)) {
            mediaArray = investor.images;
        } else if (investor.images && typeof investor.images === 'string') {
            try {
                const parsed = JSON.parse(investor.images);
                mediaArray = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                mediaArray = investor.images.split(',').map(img => img.trim()).filter(img => img);
            }
        }
        
        const images = mediaArray.filter(item => isImage(item));
        const videos = mediaArray.filter(item => isVideo(item));
        
        return { images, videos };
    };
    
    // Handle next image in carousel
    const handleNextImage = (e) => {
        e.stopPropagation();
        if (!selectedInvestor) return;
        
        const { images } = getInvestorMedia(selectedInvestor);
        if (images.length <= 1) return;
        
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };
    
    // Handle previous image in carousel
    const handlePrevImage = (e) => {
        e.stopPropagation();
        if (!selectedInvestor) return;
        
        const { images } = getInvestorMedia(selectedInvestor);
        if (images.length <= 1) return;
        
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    // Handle download reports
    const handleDownloadReports = () => {
        // Implement report download functionality
        console.log('Downloading investor reports...');
    };

    return (
        <Box m="20px">
            <Header title="INVESTOR LEADS" subtitle={`Manage Investor Leads (${totalRecords} total)`} />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: '#FFFFFF' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Investor Name</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Property Type</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Investment Amount</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Contact Number</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Created Date</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                                {investorData.map((investor) => (
                                    <TableRow key={investor.Investor_ID}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{investor.Investor_Name}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{investor.Property_Type}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Rs. {investor.Investment_Amount?.toLocaleString()}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{investor.Investor_Contact_Number}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{new Date(investor.IsCreated).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={1}>
                                            <IconButton 
                                                onClick={() => openViewModal(investor)}
                                                sx={{ 
                                                    color: '#000000',
                                                    '&:hover': {
                                                        backgroundColor: '#F5F5F5'
                                                    }
                                                }}
                                            >
                                                <VisibilityOutlinedIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!loading && (
                <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination
                        count={Math.ceil(totalRecords / pageSize)} 
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: '#000000',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: '#F5F5F5'
                                }
                            },
                            '& .MuiPaginationItem-root.Mui-selected': {
                                backgroundColor: '#D32F2F',
                                color: '#FFFFFF',
                                '&:hover': {
                                    backgroundColor: '#B71C1C'
                                }
                            },
                            '& .MuiPaginationItem-root.Mui-disabled': {
                                color: '#9E9E9E'
                            }
                        }}
                            />
            </Box>
            )}

            {/* View Investor Modal */}
            <Modal open={isViewModalOpen} onClose={closeViewModal}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: '800px',
                    bgcolor: '#FFFFFF',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    {selectedInvestor && (
                        <>
                            <Typography variant="h6" component="h2" mb={2} color="#000000">
                                Investor Details
                            </Typography>

                            {getInvestorMedia(selectedInvestor).images.length > 0 && (
                                <Box position="relative" mb={3}>
                                    <img
                                        src={getImageUrl(getInvestorMedia(selectedInvestor).images[currentImageIndex])}
                                        alt={`Investor ${currentImageIndex + 1}`}
                                        style={{
                                            width: '100%',
                                            height: '300px',
                                            objectFit: 'cover',
                                            borderRadius: '4px'
                                        }}
                                    />
                                    {getInvestorMedia(selectedInvestor).images.length > 1 && (
                                        <>
                                            <IconButton
                                                onClick={handlePrevImage}
                                                sx={{
                                                    position: 'absolute',
                                                    left: 8,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: '#FFFFFF',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,0,0,0.7)'
                                                    }
                                                }}
                                            >
                                                <NavigateBeforeIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={handleNextImage}
                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: '#FFFFFF',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,0,0,0.7)'
                                                    }
                                }}
                            >
                                                <NavigateNextIcon />
                                            </IconButton>
                                        </>
                                    )}
                            </Box>
                            )}

                            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Investor ID</Typography>
                                    <Typography color="#000000">{selectedInvestor.Investor_ID}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Investor Name</Typography>
                                    <Typography color="#000000">{selectedInvestor.Investor_Name}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Property Type</Typography>
                                    <Typography color="#000000">{selectedInvestor.Property_Type}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Investment Amount</Typography>
                                    <Typography color="#000000">Rs. {selectedInvestor.Investment_Amount?.toLocaleString()}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Contact Number</Typography>
                                    <Typography color="#000000">{selectedInvestor.Investor_Contact_Number}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Created Date</Typography>
                                    <Typography color="#000000">{new Date(selectedInvestor.IsCreated).toLocaleDateString()}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Last Updated</Typography>
                                    <Typography color="#000000">{new Date(selectedInvestor.IsUpdated).toLocaleDateString()}</Typography>
                                </Box>
                            </Box>

                            <Box display="flex" justifyContent="flex-end" mt={3}>
                                <Button 
                                    variant="contained" 
                                    onClick={closeViewModal}
                                    sx={{
                                        backgroundColor: '#D32F2F',
                                        '&:hover': {
                                            backgroundColor: '#B71C1C'
                                        }
                                    }}
                                >
                                    Close
                                </Button>
                        </Box>
                        </>
                    )}
                </Box>
            </Modal>
        </Box>
    );
};

export default InvestPage;
